import React, { FC, useEffect, useState } from 'react';
import { Box, Divider, Skeleton, Text } from '@hopskipdrive/react-components';
import { Button } from '@rideiq/shared/components/Button';

import { analyticsAddRiderContact, analyticsClickAddRiderContact } from '../../../api/services/Analytics';
import { ApiResponse } from '../../../api/utils/responseTransform';
import { actions, Can, subjects } from '../../../contexts/AbilityContext';
import { useFlags } from '../../../contexts/LDContext';
import { useToastStore } from '../../../shared/stores/toastStore';
import { RiderCard } from '../../components/RiderCard';

import { Contact } from './Contact';
import { NewContactModal } from './NewContactModal';

export const Contacts: FC<{
  isFetching: boolean;
  riderContacts: RidersContact[];
  riderFullName: string;
  riderSlug: string;
  updateContacts: (data) => Promise<ApiResponse<{
    message?: string | undefined;
  }> | null>;
}> = ({ isFetching, riderContacts, riderFullName, riderSlug, updateContacts }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { iepStructuredNotesEnabled } = useFlags();
  const useNewForm = iepStructuredNotesEnabled;
  const { resetToasts } = useToastStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [contacts, setContacts] = useState(riderContacts);

  useEffect(() => {
    setContacts(riderContacts);
  }, [riderContacts]);

  useEffect(() => {
    !isModalOpen && resetToasts();
    isModalOpen && analyticsClickAddRiderContact();
  }, [isModalOpen]);

  const handleSaveContact = async (data) => {
    setIsSubmitting(true);
    let updatedContacts = [...contacts];

    updatedContacts = [
      ...updatedContacts,
      {
        ...data,
        fullName: `${data.firstName} ${data.lastName}`,
        email: data.role === 'caregiver' ? data.email : '',
      },
    ];
    const response = await updateContacts({ passengersContactsAttributes: updatedContacts });

    if (response?.type === 'success') {
      setIsModalOpen(false);
      analyticsAddRiderContact(data, updatedContacts);
    }
    setIsSubmitting(false);
  };

  return (
    <>
      <RiderCard
        gradientOverlay={true}
        heading={
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Text variant="h3" fontWeight={500}>
              Contacts
            </Text>
            {!useNewForm && (
              <Can I={actions.add} a={subjects.rider}>
                {contacts.length < 5 && (
                  <Button
                    color="secondary"
                    onClick={() => setIsModalOpen(true)}
                    data-testid="add-contacts"
                    disabled={isFetching}
                    size="small"
                    style={{ minWidth: '68px', height: '25px' }}
                    aria-label="Add contact for rider"
                  >
                    Add
                  </Button>
                )}
              </Can>
            )}
          </Box>
        }
        height="227px"
        data-testid="rider-contacts-card"
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}></Box>
        {isFetching && <ContactSkeleton />}
        {!isFetching && !contacts.some((contact) => contact.role === 'caregiver') && (
          <Box>
            <Text fontWeight={300} textColor="red40">
              <i>No Caregiver contact set.</i>
            </Text>
            <Text fontWeight={300} textColor="secondaryText">
              Caregivers can monitor rides, be contacted by CareDrivers, and cancel rides. Emergency Contacts are only
              contacted in emergency events where Caregivers cannot be reached.
            </Text>
            <Divider my={3} />
          </Box>
        )}
        {!isFetching &&
          contacts
            .sort((a, b) => a.role.localeCompare(b.role))
            .map((contact) => (
              <React.Fragment key={contact.id}>
                <Contact
                  contacts={contacts}
                  contact={contact}
                  riderSlug={riderSlug}
                  riderFullName={riderFullName}
                  updateContacts={updateContacts}
                />
                <Divider mb={3} aria-hidden="true" />
              </React.Fragment>
            ))}
      </RiderCard>
      <NewContactModal
        isOpen={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onSubmit={handleSaveContact}
        isSubmitting={isSubmitting}
      />
    </>
  );
};

const ContactSkeleton = () => (
  <Box display="flex" justifyContent="space-between" pb={3}>
    <Box display="flex" flexDirection="column">
      <Skeleton width={100} />
      <Skeleton width={100} />
    </Box>
    <Skeleton width={100} />
  </Box>
);
